// By Management Company browse view (BUILD_SPEC §5).
// Card grid of all ~650 mgmt companies, each with 4 KPIs.
// Sortable by any KPI (default: Opps desc). Filterable by name.

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { useAuth } from '../../context/AuthContext';
import DirectoryTopNav from '../components/DirectoryTopNav';
import { usePhoneLimit, ShowMore } from '../../mobile';

const SORTS = [
  { key: 'opps', label: 'Opportunities', dir: 'desc' },
  { key: 'apollo', label: 'Apollo Customers', dir: 'desc' },
  { key: 'props', label: 'Total Properties', dir: 'desc' },
  { key: 'coverage', label: 'Coverage %', dir: 'desc' },
  { key: 'name', label: 'Name (A→Z)', dir: 'asc' },
];

export default function DirectoryCompaniesPage() {
  const { isSuperAdmin } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState('opps');
  const [exporting, setExporting] = useState(false);

  async function onExport() {
    setExporting(true);
    try {
      await api.downloadDirectoryCsv({ view: 'companies', q: filter || undefined });
    } catch (err) {
      alert(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.getDirectoryCompanies()
      .then(d => { if (!cancelled) setCompanies(d.companies || []); })
      .catch(err => { if (!cancelled) setError(err?.responseBody?.error || err.message || 'Failed to load companies'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let list = companies;
    if (q) list = list.filter(c => c.name.toLowerCase().includes(q));
    const cmp = (a, b) => {
      switch (sortKey) {
        case 'opps':     return b.opportunity_count - a.opportunity_count;
        case 'apollo':   return b.apollo_customer_count - a.apollo_customer_count;
        case 'props':    return b.property_count - a.property_count;
        case 'coverage': return b.coverage_pct - a.coverage_pct;
        case 'name':     return a.name.localeCompare(b.name);
        default:         return 0;
      }
    };
    return list.slice().sort(cmp);
  }, [companies, filter, sortKey]);
  // 652 companies = a 125,000px page on a phone. First 40 + Show more.
  const { shown, rest, more } = usePhoneLimit(visible, visible);

  return (
    <div className="directory-page">
      <DirectoryTopNav />

      <div className="directory-companies__toolbar">
        <h1 className="directory-companies__title">Management Companies</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <p className="directory-companies__count">
            {loading ? 'Loading...' : `${visible.length} of ${companies.length}`}
          </p>
          {isSuperAdmin ? (
            <button
              type="button"
              className="btn btn-sm directory-export-btn"
              onClick={onExport}
              disabled={exporting}
            >
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          ) : null}
        </div>
      </div>

      <div className="directory-companies__controls">
        <input
          type="search"
          className="directory-companies__filter"
          placeholder="Filter by company name..."
          value={filter}
          onChange={e => setFilter(e.target.value)}
          aria-label="Filter management companies"
        />
        <label className="directory-companies__sort">
          <span className="directory-companies__sort-label">Sort by</span>
          <select
            className="directory-companies__sort-select"
            value={sortKey}
            onChange={e => setSortKey(e.target.value)}
          >
            {SORTS.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </label>
      </div>

      {error ? (
        <div className="card" style={{ padding: 16, color: '#dc2626' }}>{error}</div>
      ) : null}

      <div className="directory-company-grid">
        {shown.map(c => <CompanyCard key={c.id} company={c} />)}
        {!loading && visible.length === 0 ? (
          <div className="directory-companies__empty">No companies match &ldquo;{filter}&rdquo;.</div>
        ) : null}
      </div>
      <ShowMore rest={rest} onMore={more} />
    </div>
  );
}

function CompanyCard({ company }) {
  const opps = company.opportunity_count;
  return (
    <Link to={`/sales/directory/companies/${encodeURIComponent(company.id)}`} className="directory-company-card">
      <h3 className="directory-company-card__name">{company.name}</h3>
      <div className="directory-company-card__kpis">
        <Kpi value={company.property_count} label="Total Props" />
        <Kpi value={company.apollo_customer_count} label="Apollo" accent="green" />
        <Kpi value={opps} label="Opps" accent={opps > 0 ? 'amber' : null} />
        <Kpi value={`${company.coverage_pct}%`} label="Coverage" />
      </div>
    </Link>
  );
}

function Kpi({ value, label, accent }) {
  const cls = 'directory-company-card__kpi' + (accent ? ` directory-company-card__kpi--${accent}` : '');
  return (
    <div className={cls}>
      <div className="directory-company-card__kpi-value">{value}</div>
      <div className="directory-company-card__kpi-label">{label}</div>
    </div>
  );
}
